import React, { useState } from 'react';
import { Box, IconButton, Popover, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/pro-light-svg-icons';
import { AI_DISCLAIMER } from './constants';

/**
 * Reads a resource's sources back as a sentence: "one", "one and two", or
 * "one, two, and three".
 * @param {Array<string>} resources The resources the tool was generated from
 * @returns {String} The list, written out
 */
const formatResources = (resources) =>
  new Intl.ListFormat('en', { style: 'long', type: 'conjunction' }).format(resources);

/**
 * The ⓘ beside a generated resource's heading, and the panel it opens: what the
 * resource was made from, whether anyone has edited it since, whatever the page
 * has to say for itself, and the disclaimer every generated tool carries.
 *
 * Shared by every kind of study tool, so it says only what they all have in
 * common — a page adds its own line through `instructions`.
 *
 * Props:
 *   id           (string) - ID prefix for generated element IDs.
 *   name         (string) - The resource's own name. Always shown: a heading
 *                           cut short cannot be hovered on a phone, so this
 *                           panel is where the whole of it is read.
 *   nameLabel    (string) - What to call the resource on that line.
 *   resources    (array)  - The resources the tool was generated from. Always
 *                           listed, whether or not the title already names them:
 *                           this panel is the one place they are certain to be,
 *                           and on a phone it is the only way to read them.
 *   lastEditedBy (string) - Optional; set once a user has edited the tool.
 *   instructions (node)   - Optional; the page's own line, between the two.
 *   disclaimer   (node)   - The notice at the foot of the panel.
 */
function ResourceInfoPopover({
  id,
  name,
  nameLabel = 'Name',
  resources,
  lastEditedBy,
  instructions,
  disclaimer = AI_DISCLAIMER,
}) {
  const [anchorEl, setAnchorEl] = useState(null);

  return (
    <>
      <IconButton
        size="small"
        className="resourceInfoButton"
        id={`${id}__button_info`}
        aria-label="Resource info"
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        <FontAwesomeIcon icon={faCircleInfo} />
      </IconButton>

      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        className="mss__resourceInfoPopover"
      >
        <Box className="resourceInfoContent">
          {name && (
            <Typography className="infoName" id={`${id}__text_name`}>
              <strong>{nameLabel}:</strong> {name}
            </Typography>
          )}

          {resources?.length > 0 && (
            <Typography className="infoCreatedWith" id={`${id}__text_resources`}>
              <strong>Created with:</strong> {formatResources(resources)}.
            </Typography>
          )}

          {/*
            A line of its own rather than the tail of the one above: a tool can
            have been edited whether or not it lists what it was made from, and
            tucked inside that sentence it would disappear along with it.
          */}
          {lastEditedBy && (
            <Typography className="infoEditedBy" id={`${id}__text_editedBy`}>
              <em>Edited by user.</em>
            </Typography>
          )}

          {instructions && <Typography className="infoInstructions">{instructions}</Typography>}

          <Typography className="infoDisclaimer">{disclaimer}</Typography>
        </Box>
      </Popover>
    </>
  );
}

export default ResourceInfoPopover;
